/**
 * FieldRow — uma linha de parâmetro do catálogo da Config (brief §1.4).
 *
 * Gramática fixa: label 38% | controle 62%; microcopy do efeito abaixo do
 * controle; meta-linha mono `default: X · escopo: fabrica` + chip de origem
 * (Padrão neutro / Personalizado info + "↺ Restaurar padrão" = PUT com o
 * default). Controles por tipo do catálogo — editores JSON são SEMPRE
 * estruturados (linhas add/remove), nunca textarea de JSON cru.
 */
import React from "react";
import { FiX } from "react-icons/fi";
import { Link } from "react-router-dom";

import {
  CATEGORIAS_6M,
  DRIVERS_COEFICIENTE,
  ETAPA_LABELS,
  ETAPAS_F6,
  CATALOGO_ETAPAS_INFO,
  FASE_INFO,
  MOTIVOS_CONGELAMENTO,
} from "../../../services/producaoV2";
import { CATEGORIAS as CATEGORIAS_OSE } from "../../../services/terceirizacao";
import { Pill } from "../../Orcamentos/styles";
import {
  BotaoRemoverLinha,
  ChipToggle,
  FieldAjuda,
  FieldControleCol,
  FieldErro,
  FieldLabelCol,
  FieldMeta,
  FieldRowBox,
  InputConfig,
  LinhaChips,
  LinhaEditor,
  LinkDiscreto,
  ListaEditor,
  SelectConfig,
  ToggleSwitch,
} from "./ui";

const DIAS_ISO = [
  { iso: 1, label: "Seg" },
  { iso: 2, label: "Ter" },
  { iso: 3, label: "Qua" },
  { iso: 4, label: "Qui" },
  { iso: 5, label: "Sex" },
  { iso: 6, label: "Sáb" },
  { iso: 7, label: "Dom" },
];

/** Resumo curto do default para a meta-linha (JSONs não viram stringify). */
export function resumoDefault(entrada) {
  const resumosFixos = {
    dias_uteis: "Seg–Sex",
    intervalos: "12:00–13:00 não pago",
    feriados: "Configurações > Calendário",
    ttl_por_motivo_dias: "TTL por motivo",
    horas_retrabalho_por_categoria: "horas por categoria 6M",
    coeficiente_prior_por_etapa: "vazio (= aprender pela bipagem)",
    tambor_fixo_etapa: "automático (bipagem)",
    ritmo_alvo_ambientes_dia: "sem meta",
    checkpoints_bipagem: "Usinagem + Separação",
    motivos_congelamento_pausam: "política por motivo",
    mapa_categoria_ose_etapa: "etapa por categoria de OSE",
  };
  if (resumosFixos[entrada.chave]) return resumosFixos[entrada.chave];
  if (entrada.default === null || entrada.default === undefined) return "–";
  return String(entrada.default);
}

// ---------------------------------------------------------------------------
// Editores estruturados por chave
// ---------------------------------------------------------------------------

function EditorDiasUteis({ valor, onChange, disabled }) {
  const dias = Array.isArray(valor) ? valor : [];
  return (
    <LinhaChips>
      {DIAS_ISO.map((d) => {
        const ativo = dias.includes(d.iso);
        return (
          <ChipToggle
            key={d.iso}
            type="button"
            $ativo={ativo}
            disabled={disabled}
            aria-pressed={ativo}
            onClick={() =>
              onChange(
                ativo
                  ? dias.filter((x) => x !== d.iso)
                  : [...dias, d.iso].sort((a, b) => a - b),
              )
            }
          >
            {d.label}
          </ChipToggle>
        );
      })}
    </LinhaChips>
  );
}

// Feriados e recessos saíram daqui: o calendário é único da unidade e a
// Produção, as Solicitações e a Capacidade da Fábrica leem o mesmo. O backend
// recusa (400) gravar `feriados` por esta tela.
export const ROTA_CALENDARIO_UNIDADE = "/configuracoes/calendario";
export const CHAVE_FERIADOS = "feriados";
// Origem que o backend devolve para chave lida do calendário da unidade.
export const ORIGEM_CALENDARIO = "calendario";

function FeriadosNoCalendario() {
  return (
    <FieldAjuda>
      Editado em <Link to={ROTA_CALENDARIO_UNIDADE}>Configurações &gt; Calendário</Link>.
    </FieldAjuda>
  );
}

function EditorIntervalos({ valor, onChange, disabled }) {
  const intervalos = Array.isArray(valor) ? valor : [];
  const atualizar = (idx, campo, v) => {
    const novo = intervalos.map((item, i) =>
      i === idx ? { ...item, [campo]: v } : item,
    );
    onChange(novo);
  };
  return (
    <ListaEditor>
      {intervalos.map((item, idx) => (
        <LinhaEditor key={idx}>
          <InputConfig
            type="time"
            value={item.inicio || ""}
            disabled={disabled}
            onChange={(e) => atualizar(idx, "inicio", e.target.value)}
            aria-label="Início do intervalo"
          />
          <span>–</span>
          <InputConfig
            type="time"
            value={item.fim || ""}
            disabled={disabled}
            onChange={(e) => atualizar(idx, "fim", e.target.value)}
            aria-label="Fim do intervalo"
          />
          <ToggleSwitch
            checked={Boolean(item.pago)}
            disabled={disabled}
            onChange={(v) => atualizar(idx, "pago", v)}
            rotulo="pago"
          />
          <BotaoRemoverLinha
            type="button"
            disabled={disabled}
            aria-label="Remover intervalo"
            onClick={() => onChange(intervalos.filter((_, i) => i !== idx))}
          >
            <FiX size={14} aria-hidden="true" />
          </BotaoRemoverLinha>
        </LinhaEditor>
      ))}
      {!disabled && (
        <LinhaEditor>
          <LinkDiscreto
            type="button"
            onClick={() =>
              onChange([
                ...intervalos,
                { inicio: "12:00", fim: "13:00", pago: false },
              ])
            }
          >
            + Adicionar intervalo
          </LinkDiscreto>
        </LinhaEditor>
      )}
    </ListaEditor>
  );
}

function EditorTtlPorMotivo({ valor, onChange, disabled }) {
  const mapa = valor && typeof valor === "object" ? valor : {};
  return (
    <ListaEditor>
      {MOTIVOS_CONGELAMENTO.map((motivo) => {
        const dias = mapa[motivo.codigo];
        return (
          <LinhaEditor key={motivo.codigo}>
            <span className="rotulo" title={motivo.label}>
              {motivo.label}
            </span>
            <InputConfig
              type="number"
              min={1}
              style={{ maxWidth: 90 }}
              value={dias === null || dias === undefined ? "" : dias}
              disabled={disabled}
              placeholder="sem TTL"
              onChange={(e) =>
                onChange({
                  ...mapa,
                  [motivo.codigo]:
                    e.target.value === "" ? null : Number(e.target.value),
                })
              }
              aria-label={`TTL em dias — ${motivo.label}`}
            />
            <span style={{ fontSize: 12, color: "var(--ink-4)" }}>dias</span>
          </LinhaEditor>
        );
      })}
    </ListaEditor>
  );
}

function EditorHorasRetrabalho({ valor, onChange, disabled }) {
  const mapa = valor && typeof valor === "object" ? valor : {};
  return (
    <ListaEditor>
      {CATEGORIAS_6M.map((categoria) => (
        <LinhaEditor key={categoria.codigo}>
          <span className="rotulo" title={categoria.label}>
            {categoria.label}
          </span>
          <InputConfig
            type="number"
            min={0}
            step="0.5"
            style={{ maxWidth: 90 }}
            value={mapa[categoria.codigo] ?? ""}
            disabled={disabled}
            onChange={(e) =>
              onChange({
                ...mapa,
                [categoria.codigo]:
                  e.target.value === "" ? 0 : Number(e.target.value),
              })
            }
            aria-label={`Horas de retrabalho — ${categoria.label}`}
          />
          <span style={{ fontSize: 12, color: "var(--ink-4)" }}>h</span>
        </LinhaEditor>
      ))}
    </ListaEditor>
  );
}

/**
 * Checkpoints de bipagem (Lote B — §2.5): multi-select estruturado das
 * etapas BIPÁVEIS (F5/F6), na ordem do roteiro. Etapa marcada = só conclui
 * pela bipagem no Terminal; desmarcada = conclusão manual pelo responsável
 * da fase. Nunca textarea de JSON cru (regra do brief §1.4).
 */
function EditorCheckpointsBipagem({ valor, onChange, disabled }) {
  const selecionadas = Array.isArray(valor) ? valor : [];
  const bipaveis = CATALOGO_ETAPAS_INFO.filter((e) => e.bipavel);
  return (
    <LinhaChips>
      {bipaveis.map((etapa) => {
        const ativo = selecionadas.includes(etapa.codigo);
        return (
          <ChipToggle
            key={etapa.codigo}
            type="button"
            $ativo={ativo}
            disabled={disabled}
            aria-pressed={ativo}
            title={`${etapa.label} (${FASE_INFO[etapa.fase]?.curto || etapa.fase})`}
            onClick={() =>
              onChange(
                ativo
                  ? selecionadas.filter((c) => c !== etapa.codigo)
                  : bipaveis
                      .map((e) => e.codigo)
                      .filter(
                        (c) => selecionadas.includes(c) || c === etapa.codigo,
                      ),
              )
            }
          >
            {etapa.label}
          </ChipToggle>
        );
      })}
    </LinhaChips>
  );
}

/**
 * Política de pausa do relógio do buffer por motivo de congelamento
 * (Lote D — I-2): linha rótulo → toggle. Ligado = congelar por esse
 * motivo PAUSA o relógio (tempo parado não consome buffer); desligado =
 * o relógio segue correndo. Chaves fora do catálogo de motivos são
 * preservadas no valor (spread) — nunca perdemos configuração ao salvar.
 */
function EditorMotivosCongelamentoPausam({ valor, onChange, disabled }) {
  const mapa = valor && typeof valor === "object" ? valor : {};
  return (
    <ListaEditor>
      {MOTIVOS_CONGELAMENTO.map((motivo) => {
        const pausa = Boolean(mapa[motivo.codigo]);
        return (
          <LinhaEditor key={motivo.codigo}>
            <span className="rotulo" title={motivo.label}>
              {motivo.label}
            </span>
            <ToggleSwitch
              checked={pausa}
              disabled={disabled}
              onChange={(v) => onChange({ ...mapa, [motivo.codigo]: v })}
              rotulo={pausa ? "pausa o relógio" : "relógio corre"}
            />
          </LinhaEditor>
        );
      })}
    </ListaEditor>
  );
}

/**
 * Mapa categoria de OSE → etapa marcada no retorno do terceiro (Lote D —
 * I-6: hoje tudo vira Pintura). Linha categoria → select de etapa do
 * catálogo; "Padrão do sistema" remove a chave do mapa (backend usa o
 * default dele). Nunca textarea de JSON cru (regra do brief §1.4).
 */
function EditorMapaCategoriaOse({ valor, onChange, disabled }) {
  const mapa = valor && typeof valor === "object" ? valor : {};
  return (
    <ListaEditor>
      {CATEGORIAS_OSE.map((categoria) => (
        <LinhaEditor key={categoria.value}>
          <span className="rotulo" title={categoria.label}>
            {categoria.label}
          </span>
          <SelectConfig
            value={mapa[categoria.value] || ""}
            disabled={disabled}
            onChange={(e) => {
              const novo = { ...mapa };
              if (e.target.value === "") delete novo[categoria.value];
              else novo[categoria.value] = e.target.value;
              onChange(novo);
            }}
            aria-label={`Etapa marcada — ${categoria.label}`}
          >
            <option value="">Padrão do sistema</option>
            {CATALOGO_ETAPAS_INFO.map((etapa) => (
              <option key={etapa.codigo} value={etapa.codigo}>
                {etapa.label} ({FASE_INFO[etapa.fase]?.curto || etapa.fase})
              </option>
            ))}
          </SelectConfig>
        </LinhaEditor>
      ))}
    </ListaEditor>
  );
}

/** Prior por etapa (valor + driver) — F5 industrialização + F6 marcenaria. */
function EditorCoeficientePrior({ valor, onChange, disabled }) {
  const mapa = valor && typeof valor === "object" ? valor : {};
  const etapas = [
    ...CATALOGO_ETAPAS_INFO.filter(
      (e) => e.fase === "INDUSTRIALIZACAO" && e.bipavel,
    ),
    ...ETAPAS_F6,
  ];
  const atualizar = (codigo, campo, v) => {
    const atual = mapa[codigo] || { valor: "", driver: "min_peca" };
    const novoItem = { ...atual, [campo]: v };
    const novo = { ...mapa };
    if (campo === "valor" && (v === "" || v === null)) {
      delete novo[codigo]; // sem valor = sem prior (aprende pela bipagem)
    } else {
      novo[codigo] = {
        valor: campo === "valor" ? Number(v) : Number(novoItem.valor) || "",
        driver: novoItem.driver || "min_peca",
      };
      if (novo[codigo].valor === "" || Number.isNaN(novo[codigo].valor)) {
        delete novo[codigo];
      }
    }
    onChange(novo);
  };
  return (
    <ListaEditor>
      {etapas.map((etapa) => {
        const item = mapa[etapa.codigo];
        return (
          <LinhaEditor key={etapa.codigo}>
            <span className="rotulo" title={etapa.label}>
              {etapa.label}
            </span>
            <InputConfig
              type="number"
              min={0}
              step="0.1"
              style={{ maxWidth: 90 }}
              value={item?.valor ?? ""}
              disabled={disabled}
              placeholder="aprender"
              onChange={(e) => atualizar(etapa.codigo, "valor", e.target.value)}
              aria-label={`Prior — ${etapa.label}`}
            />
            <SelectConfig
              value={item?.driver || "min_peca"}
              disabled={disabled || !item}
              style={{ maxWidth: 140 }}
              onChange={(e) => atualizar(etapa.codigo, "driver", e.target.value)}
              aria-label={`Driver — ${etapa.label}`}
            >
              {DRIVERS_COEFICIENTE.map((d) => (
                <option key={d.codigo} value={d.codigo}>
                  {d.label}
                </option>
              ))}
            </SelectConfig>
          </LinhaEditor>
        );
      })}
    </ListaEditor>
  );
}

// ---------------------------------------------------------------------------
// Controle genérico por tipo
// ---------------------------------------------------------------------------

function Controle({ entrada, valor, onChange, disabled, erro }) {
  const { tipo, chave } = entrada;

  if (chave === "dias_uteis") {
    return <EditorDiasUteis valor={valor} onChange={onChange} disabled={disabled} />;
  }
  if (chave === CHAVE_FERIADOS) {
    return <FeriadosNoCalendario />;
  }
  if (chave === "intervalos") {
    return <EditorIntervalos valor={valor} onChange={onChange} disabled={disabled} />;
  }
  if (chave === "ttl_por_motivo_dias") {
    return <EditorTtlPorMotivo valor={valor} onChange={onChange} disabled={disabled} />;
  }
  if (chave === "horas_retrabalho_por_categoria") {
    return (
      <EditorHorasRetrabalho valor={valor} onChange={onChange} disabled={disabled} />
    );
  }
  if (chave === "coeficiente_prior_por_etapa") {
    return (
      <EditorCoeficientePrior valor={valor} onChange={onChange} disabled={disabled} />
    );
  }
  if (chave === "checkpoints_bipagem") {
    return (
      <EditorCheckpointsBipagem
        valor={valor}
        onChange={onChange}
        disabled={disabled}
      />
    );
  }
  if (chave === "motivos_congelamento_pausam") {
    return (
      <EditorMotivosCongelamentoPausam
        valor={valor}
        onChange={onChange}
        disabled={disabled}
      />
    );
  }
  if (chave === "mapa_categoria_ose_etapa") {
    return (
      <EditorMapaCategoriaOse
        valor={valor}
        onChange={onChange}
        disabled={disabled}
      />
    );
  }

  if (tipo === "time") {
    return (
      <InputConfig
        type="time"
        $erro={Boolean(erro)}
        value={valor || ""}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  if (tipo === "int" || tipo === "decimal") {
    return (
      <InputConfig
        type="number"
        $erro={Boolean(erro)}
        step={tipo === "int" ? 1 : "0.01"}
        value={valor === null || valor === undefined ? "" : valor}
        placeholder={entrada.permite_nulo ? "sem valor" : undefined}
        disabled={disabled}
        onChange={(e) => {
          const bruto = e.target.value;
          if (bruto === "") {
            onChange(entrada.permite_nulo ? null : "");
            return;
          }
          onChange(Number(bruto));
        }}
      />
    );
  }

  if (tipo === "bool") {
    return (
      <ToggleSwitch
        checked={Boolean(valor)}
        disabled={disabled}
        onChange={onChange}
        rotulo={valor ? "Ativado" : "Desativado"}
      />
    );
  }

  if (tipo === "choice") {
    const rotuloEtapa = (codigo) =>
      ETAPA_LABELS[codigo]
        ? `${ETAPA_LABELS[codigo]} (${FASE_INFO[CATALOGO_ETAPAS_INFO.find((e) => e.codigo === codigo)?.fase]?.curto || ""})`
        : codigo;
    return (
      <SelectConfig
        $erro={Boolean(erro)}
        value={valor === null || valor === undefined ? "" : valor}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
      >
        {entrada.permite_nulo && (
          <option value="">Automático (derivado da bipagem)</option>
        )}
        {(entrada.choices || []).map((c) => (
          <option key={c} value={c}>
            {rotuloEtapa(c)}
          </option>
        ))}
      </SelectConfig>
    );
  }

  // Defensivo — tipo desconhecido do catálogo (nunca textarea de JSON cru).
  return (
    <FieldAjuda>
      Tipo de controle não suportado nesta versão ({tipo}).
    </FieldAjuda>
  );
}

// ---------------------------------------------------------------------------
// FieldRow
// ---------------------------------------------------------------------------

export default function FieldRow({
  entrada,
  valor,
  editado,
  erro,
  disabled,
  encontrado,
  onChange,
  onRestaurar,
}) {
  const personalizado = entrada.origem === "personalizado";
  // Somente leitura aqui: "Restaurar padrão" gravaria `feriados` e o backend recusa.
  const podeRestaurar = !disabled && entrada.chave !== CHAVE_FERIADOS;
  return (
    <FieldRowBox $encontrado={encontrado} data-chave={entrada.chave}>
      <FieldLabelCol>{entrada.label}</FieldLabelCol>
      <FieldControleCol>
        <Controle
          entrada={entrada}
          valor={valor}
          onChange={onChange}
          disabled={disabled}
          erro={erro}
        />
        {erro && <FieldErro>{erro}</FieldErro>}
        <FieldAjuda>{entrada.descricao}</FieldAjuda>
        <FieldMeta>
          <span>
            default: {resumoDefault(entrada)} · escopo: {entrada.escopo}
          </span>
          {entrada.origem === ORIGEM_CALENDARIO ? (
            <Pill $tone="neutral">Do calendário</Pill>
          ) : personalizado ? (
            <>
              <Pill $tone="info">Personalizado</Pill>
              {podeRestaurar && (
                <LinkDiscreto type="button" onClick={onRestaurar}>
                  ↺ Restaurar padrão
                </LinkDiscreto>
              )}
            </>
          ) : (
            <Pill $tone="neutral">Padrão</Pill>
          )}
          {editado && <Pill $tone="warning">não salvo</Pill>}
        </FieldMeta>
      </FieldControleCol>
    </FieldRowBox>
  );
}
